import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import apiService from '../../services/api';

const CustomerGroupReport = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [customerGroupsData, setCustomerGroupsData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customerGroupsList, setCustomerGroupsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Applied Filters
  const [customerGroupId, setCustomerGroupId] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pending Filters inside modal
  const [pendingCustomerGroupId, setPendingCustomerGroupId] = useState('all');
  const [pendingLocationId, setPendingLocationId] = useState('all');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');

  // Summary Metrics
  const [summary, setSummary] = useState({
    totalSaleAllGroups: 0,
    totalGroups: 0,
  });

  const fetchFilterOptions = async () => {
    try {
      const res = await apiService.getCustomerGroupReportFilterOptions();
      const options = res.data || res;
      setLocations(options.locations || []);
      setCustomerGroupsList(options.customer_groups || []);
    } catch (err) {
      /* console.error('Error fetching customer group filter options:', err); */
    }
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: entriesPerPage,
      };

      if (customerGroupId !== 'all') params.customer_group_id = customerGroupId;
      if (locationId !== 'all') params.location_id = locationId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiService.getCustomerGroupReport(params);
      const dataObj = response.data || response;
      const groupsList = dataObj.customer_groups || (Array.isArray(dataObj) ? dataObj : []);
      setCustomerGroupsData(groupsList);

      const summaryObj = dataObj.summary || {};
      const pagination = dataObj.pagination || {};

      setTotalRecords(pagination.total || groupsList.length);
      setSummary({
        totalSaleAllGroups: summaryObj.total_sale_all_groups || 0,
        totalGroups: summaryObj.total_groups || groupsList.length,
      });
    } catch (err) {
      /* console.error('Failed to fetch customer group report data', err); */
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [currentPage, entriesPerPage, customerGroupId, locationId, startDate, endDate]);

  const handleOpenFilters = () => {
    setPendingCustomerGroupId(customerGroupId);
    setPendingLocationId(locationId);
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setCustomerGroupId(pendingCustomerGroupId);
    setLocationId(pendingLocationId);
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setPendingCustomerGroupId('all');
    setPendingLocationId('all');
    setPendingStartDate('');
    setPendingEndDate('');
  };

  const filteredGroups = customerGroupsData.filter((g) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (g.customer_group_name || '').toLowerCase();
    return name.includes(term);
  });

  const tableHeaders = ['CUSTOMER GROUP NAME', 'TOTAL SALE'];

  const handleExport = (format) => {
    const headers = tableHeaders;
    const rows = filteredGroups.map((g) => [
      g.customer_group_name || 'No Group',
      `$${parseFloat(g.total_sale || 0).toFixed(2)}`,
    ]);

    const title = 'Customer Group Report';
    const filename = 'customer_group_report';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      {/* ── Summary Metric Cards ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '1.25rem',
        }}
      >
        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Sales (All Groups)</span>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#10b981', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalSaleAllGroups)}</h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Customer Groups</span>
          <h3 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#2563eb', margin: '0.2rem 0 0' }}>{summary.totalGroups}</h3>
        </div>
      </div>

      {/* ── Table Container ── */}
      <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Table controls */}
        <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem' }}>
          <div className="table-controls-left">
            <div className="entries-select">
              <span>Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Filters Button placed right before Export Button */}
            <button
              onClick={handleOpenFilters}
              style={{
                padding: '0.4rem 0.8rem',
                border: '1px solid var(--etpos-border, #e2e8f0)',
                borderRadius: '6px',
                background: 'var(--etpos-card, #fff)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: 'var(--etpos-text, #334155)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontWeight: '500'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
              </svg>
              Filters
            </button>

            <div className="export-dropdown-container" style={{ position: 'relative', zIndex: 100 }}>
              <button
                className="export-btn"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div
                  className="export-dropdown-menu"
                  style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', zIndex: 9999, minWidth: '160px' }}
                >
                  {[
                    { label: 'Export CSV', action: () => handleExport('CSV'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                    { label: 'Export Excel', action: () => handleExport('Excel'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h8"/><path d="M8 17h8"/></svg> },
                    { label: 'Print', action: () => handleExport('Print'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> },
                    { label: 'Export PDF', action: () => handleExport('PDF'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15v-6h2a2 2 0 0 1 0 4H9"/></svg> }
                  ].map((item, i) => (
                    <button
                      key={i}
                      onClick={() => { item.action(); setShowExportDropdown(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.55rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--etpos-hover, #f1f5f9)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: 'var(--etpos-hover, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                {tableHeaders.map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>
                    Loading...
                  </td>
                </tr>
              ) : filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>
                    No customer group records found
                  </td>
                </tr>
              ) : (
                filteredGroups.map((group, index) => (
                  <tr key={group.customer_group_id || index} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>
                      {group.customer_group_name || 'No Group'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: '#10b981' }}>
                      {formatCurrency(group.total_sale)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: '700' }}>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', textAlign: 'right', zIndex: 5 }}>
                  Total:
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#10b981', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalSaleAllGroups)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)' }}>
          <div className="pagination-info" style={{ fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #475569)' }}>
            Showing {filteredGroups.length > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
          </div>
          <div className="pagination-controls" style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="pagination-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', color: 'var(--etpos-text, #475569)' }}>
              Page {currentPage}
            </span>
            <button
              className="pagination-btn"
              disabled={currentPage * entriesPerPage >= totalRecords}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ── Filters Popup Modal ── */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', width: '95%', background: 'var(--etpos-card, #fff)' }}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', color: 'var(--etpos-text, #111827)' }}>
              <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filter Customer Group Report
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Customer Group:</label>
                  <select value={pendingCustomerGroupId} onChange={e => setPendingCustomerGroupId(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }}>
                    <option value="all">All Groups</option>
                    {customerGroupsList.map(cg => <option key={cg.id} value={cg.id}>{cg.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Location:</label>
                  <select
                    value={pendingLocationId}
                    onChange={(e) => setPendingLocationId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.75rem',
                      border: '1px solid var(--etpos-border, #cbd5e1)',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'var(--etpos-card, #fff)',
                      color: 'var(--etpos-text, #1e293b)'
                    }}
                  >
                    <option value="all">All Locations</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Start Date:</label>
                  <input type="date" value={pendingStartDate} onChange={e => setPendingStartDate(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>End Date:</label>
                  <input type="date" value={pendingEndDate} onChange={e => setPendingEndDate(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }} />
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', background: 'var(--etpos-card, #fff)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={handleResetFilters}
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  border: '1px solid var(--etpos-border, #cbd5e1)',
                  borderRadius: '6px',
                  background: 'var(--etpos-card, #f8fafc)',
                  color: 'var(--etpos-text, #475569)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease'
                }}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#3b82f6',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(59,130,246,0.2)',
                  transition: 'all 0.15s ease'
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerGroupReport;
